import { useCallback, useEffect, useRef, useState } from 'react';

import { usePaymentErrorContext } from '../contexts/payment-error-context';
import { PaymentProcessor } from '../core/payment-processor';
import {
    SavedPaymentMethod,
    UserAccountPaymentInstrumentsV1Dto
} from '../types';

interface UseSavedPaymentMethodsState {
    methods: SavedPaymentMethod[];
    isLoading: boolean;
    isLoaded: boolean;
    hasError: boolean;
    selectedMethodId?: string;
}

interface UseSavedPaymentMethodsResult extends UseSavedPaymentMethodsState {
    refetch: () => Promise<void>;
}

const mapPaymentInstruments = (
    data: UserAccountPaymentInstrumentsV1Dto
): SavedPaymentMethod[] => {
    return data.paymentInstruments.map((instrument) => ({
        ...instrument,
        id:
            instrument.paymentInstrumentToken ||
            `${instrument.paymentMethod}-${instrument.createdDate}`
    }));
};

const getAutoSelectedMethodId = (
    methods: SavedPaymentMethod[]
): string | undefined => {
    const paymentInstrument = methods.find(
        (method) => method.createdDate && method.paymentRequestToken
    );

    return paymentInstrument?.id;
};

export const useSavedPaymentMethods = (
    paymentProcessor: PaymentProcessor
): UseSavedPaymentMethodsResult => {
    const [state, setState] = useState<UseSavedPaymentMethodsState>({
        methods: [],
        isLoading: true,
        isLoaded: false,
        hasError: false,
        selectedMethodId: undefined
    });

    const { addError } = usePaymentErrorContext();
    const isMountedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchPaymentMethods = useCallback(async () => {
        if (isMountedRef.current) {
            setState((prev) => ({
                ...prev,
                isLoading: true,
                isLoaded: false,
                hasError: false
            }));
        }

        try {
            const data =
                await paymentProcessor.getUserAccountPaymentInstruments();
            const transformedMethods = mapPaymentInstruments(data);

            if (isMountedRef.current) {
                setState({
                    methods: transformedMethods,
                    isLoading: false,
                    isLoaded: true,
                    hasError: false,
                    selectedMethodId:
                        getAutoSelectedMethodId(transformedMethods)
                });
            }
        } catch (error) {
            if (isMountedRef.current) {
                setState({
                    methods: [],
                    isLoading: false,
                    isLoaded: false,
                    hasError: true,
                    selectedMethodId: undefined
                });
                addError(error);
            }
        }
    }, [paymentProcessor, addError]);

    useEffect(() => {
        void fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    const refetch = useCallback(async () => {
        await fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    return {
        ...state,
        refetch
    };
};
